import { EC2Client, DescribeVpcsCommand, DescribeSecurityGroupsCommand } from '@aws-sdk/client-ec2';
import { RDSClient, DescribeDBSubnetGroupsCommand as RDSDescribeDBSubnetGroupsCommand } from '@aws-sdk/client-rds';
import { getAWSConfig } from '../config/aws';

export const listVPCs = async (awsCredentials = null) => {
  const ec2Client = new EC2Client(getAWSConfig(awsCredentials));
  try {
    console.log('Fetching VPCs...');
    const command = new DescribeVpcsCommand({});
    const response = await ec2Client.send(command);
    
    const vpcs = response.Vpcs.map(vpc => ({
      vpcId: vpc.VpcId,
      cidrBlock: vpc.CidrBlock,
      state: vpc.State,
      isDefault: vpc.IsDefault,
      name: vpc.Tags?.find(tag => tag.Key === 'Name')?.Value || 'No Name'
    }));
    
    console.log(`Found ${vpcs.length} VPCs`);
    return vpcs;
  } catch (error) {
    console.error('Error listing VPCs:', error);
    throw error;
  }
};

export const listSecurityGroups = async (vpcId = null, awsCredentials = null) => {
  const ec2Client = new EC2Client(getAWSConfig(awsCredentials));
  try {
    console.log(`Fetching security groups${vpcId ? ` for VPC ${vpcId}` : ''}...`);

    const filters = [];
    if (vpcId) {
      filters.push({
        Name: 'vpc-id',
        Values: [vpcId]
      });
    }

    const command = new DescribeSecurityGroupsCommand({
      Filters: filters.length > 0 ? filters : undefined
    });

    const response = await ec2Client.send(command);
    
    const securityGroups = response.SecurityGroups.map(sg => ({
      groupId: sg.GroupId,
      groupName: sg.GroupName,
      description: sg.Description,
      vpcId: sg.VpcId
    }));
    
    console.log(`Found ${securityGroups.length} security groups`);
    return securityGroups;
  } catch (error) {
    console.error('Error listing security groups:', error);
    throw error;
  }
};

export const listDBSubnetGroups = async (awsCredentials = null) => {
  const rdsClient = new RDSClient(getAWSConfig(awsCredentials));
  try {
    console.log('Fetching DB subnet groups...');
    const command = new RDSDescribeDBSubnetGroupsCommand({});
    const response = await rdsClient.send(command);

    const subnetGroups = response.DBSubnetGroups.map(sg => ({
      name: sg.DBSubnetGroupName,
      description: sg.DBSubnetGroupDescription,
      vpcId: sg.VpcId,
      status: sg.SubnetGroupStatus,
      subnets: sg.Subnets?.map(subnet => ({
        subnetId: subnet.SubnetIdentifier,
        availabilityZone: subnet.SubnetAvailabilityZone?.Name,
        status: subnet.SubnetStatus
      }))
    }));

    console.log(`Found ${subnetGroups.length} DB subnet groups`);
    return subnetGroups;
  } catch (error) {
    console.error('Error listing DB subnet groups:', error);
    throw error;
  }
};

export const getSecurityGroupDetails = async (securityGroupId, awsCredentials = null) => {
  const ec2Client = new EC2Client(getAWSConfig(awsCredentials));
  try {
    const { DescribeSecurityGroupsCommand } = await import('@aws-sdk/client-ec2');
    const command = new DescribeSecurityGroupsCommand({
      GroupIds: [securityGroupId]
    });
    const response = await ec2Client.send(command);
    return response.SecurityGroups[0];
  } catch (error) {
    console.error('Error getting security group details:', error);
    throw error;
  }
};

export const authorizeSecurityGroupIngress = async (securityGroupId, ipAddress, port, description, awsCredentials = null) => {
  const ec2Client = new EC2Client(getAWSConfig(awsCredentials));
  try {
    const { AuthorizeSecurityGroupIngressCommand } = await import('@aws-sdk/client-ec2');
    const command = new AuthorizeSecurityGroupIngressCommand({
      GroupId: securityGroupId,
      IpPermissions: [
        {
          IpProtocol: 'tcp',
          FromPort: port,
          ToPort: port,
          IpRanges: [
            {
              CidrIp: `${ipAddress}/32`,
              Description: description
            }
          ]
        }
      ]
    });

    await ec2Client.send(command);
    console.log(`Added rule for ${ipAddress} on port ${port} to ${securityGroupId}`);
    return true;
  } catch (error) {
    console.error('Error authorizing security group ingress:', error);
    throw error;
  }
};

export const revokeSecurityGroupIngress = async (securityGroupId, ipAddress, port, awsCredentials = null) => {
  const ec2Client = new EC2Client(getAWSConfig(awsCredentials));
  try {
    const { RevokeSecurityGroupIngressCommand } = await import('@aws-sdk/client-ec2');
    const command = new RevokeSecurityGroupIngressCommand({
      GroupId: securityGroupId,
      IpPermissions: [
        {
          IpProtocol: 'tcp',
          FromPort: port,
          ToPort: port,
          IpRanges: [
            {
              CidrIp: `${ipAddress}/32`
            }
          ]
        }
      ]
    });

    await ec2Client.send(command);
    console.log(`Revoked rule for ${ipAddress} on port ${port} from ${securityGroupId}`);
    return true;
  } catch (error) {
    // Don't throw error if rule doesn't exist
    if (error.name === 'InvalidPermission.NotFound') {
      console.log(`Rule for ${ipAddress} on port ${port} not found in ${securityGroupId}`);
      return false;
    }
    console.error('Error revoking security group ingress:', error);
    throw error;
  }
};